import {
  NookApiError,
  useCreateLinkCode,
  useDeleteAccount,
  useCreateMcpToken,
  useLinkedChannels,
  useMcpTokens,
  useMe,
  useOpenBillingPortal,
  useRequestExport,
  useRevokeMcpToken,
  useStartCheckout,
  useStorageUsage,
  useUnlinkChannel,
  type CheckoutPlan,
  type LinkedChannel,
  type McpToken,
  type User,
} from "@nook/api-client"
import { formatBytes } from "@nook/client-ui"
import { formatUatThroughDate } from "@nook/client-ui/core"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Progress,
  SidebarTrigger,
  Skeleton,
  toast,
} from "@nook/ui"
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router"
import { Check, CircleCheck, Copy, Download, Info, RefreshCw, X } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { useEffect, useRef, useState, type ReactNode } from "react"

/**
 * `/settings` (block 24). Flat sections, no cards and no Tabs (block 27 override 10).
 * Billing is a section here, not `/billing` (block 31 override of C6); Dodo's
 * checkout returns here with `?checkout=`.
 */
export const Route = createFileRoute("/_authed/settings")({
  validateSearch: (search): { checkout?: "success" | "cancelled" } =>
    search.checkout === "success" || search.checkout === "cancelled" ? { checkout: search.checkout } : {},
  component: Settings,
})

function Settings() {
  const requestExport = useRequestExport()

  const onExport = () =>
    requestExport.mutate(undefined, {
      onError: (error) =>
        toast.error(
          error instanceof NookApiError && error.code === "rate_limited"
            ? "You can export 3 times a day. Try again tomorrow."
            : "Couldn't start your export. Try again.",
        ),
    })

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-10">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="-ml-2 md:hidden" />
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </div>
      <div className="divide-border divide-y">
        <Account />
        <Billing />
        <LinkedChannels />
        <McpTokens />
        <ExportData onExport={onExport} pending={requestExport.isPending} sent={requestExport.isSuccess} />
        <DeleteAccount onExport={onExport} exported={requestExport.isSuccess || requestExport.isPending} />
      </div>
      <footer className="text-muted-foreground flex gap-4 pt-2 text-xs">
        {LEGAL.map(([path, label]) => (
          <a key={path} href={`https://www.getnook.tech/${path}`} target="_blank" rel="noreferrer" className="hover:underline underline-offset-4">
            {label}
          </a>
        ))}
      </footer>
    </div>
  )
}

function Section({
  id,
  title,
  description,
  danger,
  children,
}: {
  id?: string
  title: string
  description: string
  danger?: boolean
  children: ReactNode
}) {
  // A link to `#billing` lands on that section and marks it for a moment.
  const landed = useLocation({ select: (location) => Boolean(id) && location.hash === id })
  const [flash, setFlash] = useState(landed)
  useEffect(() => {
    if (!landed) return
    setFlash(true)
    const timer = setTimeout(() => setFlash(false), 1600)
    return () => clearTimeout(timer)
  }, [landed])

  return (
    <section
      id={id}
      className={`-mx-4 scroll-mt-6 space-y-4 rounded-xl px-4 py-8 transition-colors duration-700 ${flash ? "bg-muted/60" : ""}`}
    >
      <div className="space-y-1">
        <h2 className={`text-sm font-medium ${danger ? "text-destructive" : ""}`}>{title}</h2>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      {children}
    </section>
  )
}

/** Hosted on the marketing site (block 35, A9 C10). */
const LEGAL = [
  ["privacy", "Privacy Policy"],
  ["terms", "Terms of Service"],
  ["cookies", "Cookie Policy"],
] as const

const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" })

/** Details from `useMe` and `useStorageUsage` (A9 C1), on every plan. */
function Account() {
  const me = useMe()
  const usage = useStorageUsage()

  return (
    <Section title="Account" description="Your details and storage.">
      {me.isPending ? (
        <Skeleton className="h-20" />
      ) : me.isError ? (
        <p className="text-destructive text-sm">Couldn't load your account.</p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Email</dt>
          <dd className="truncate">{me.data.email}</dd>
          <dt className="text-muted-foreground">Member since</dt>
          <dd>{me.data.createdAt ? formatDate(me.data.createdAt) : "…"}</dd>
          <dt className="text-muted-foreground">Storage used</dt>
          <dd className="space-y-1.5">
            {usage.data ? (
              <>
                <Progress value={(usage.data.usedBytes / usage.data.limitBytes) * 100} aria-label="Storage used" />
                <p className="text-muted-foreground text-[0.8125rem]">
                  {formatBytes(usage.data.usedBytes)} of {formatBytes(usage.data.limitBytes)}
                </p>
              </>
            ) : (
              "…"
            )}
          </dd>
        </dl>
      )}
    </Section>
  )
}

/**
 * Plan status and upgrade (blocks 31, 32). On `?checkout=success`, Dodo's
 * redirect usually beats its webhook, so poll `/v1/me` every 2s until the plan
 * is `pro`, for at most 60s.
 */
function Billing() {
  const { checkout } = Route.useSearch()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (checkout !== "success") return
    const timer = setTimeout(() => setTimedOut(true), 60_000)
    return () => clearTimeout(timer)
  }, [checkout])

  const me = useMe({
    refetchInterval: (user) => (checkout !== "success" || user?.plan === "pro" || timedOut ? false : 2000),
  })

  const navigate = useNavigate()
  const confirmed = checkout === "success" && me.data?.plan === "pro"
  const notice =
    checkout === "cancelled"
      ? "Checkout was cancelled. Your plan has not changed."
      : checkout === "success"
        ? me.data?.plan === "pro"
          ? "You're on Nook Pro. Thanks for subscribing. Nook and Ask are open."
          : timedOut
            ? "We couldn't confirm an active subscription yet. If payment was completed, contact support with your receipt; otherwise, try checkout again."
            : "Confirming your subscription…"
        : null

  return (
    <Section id="billing" title="Plan & billing" description="Your plan and subscription.">
      {notice && (
        <div role="status" className="bg-card flex items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-xs">
          {confirmed ? <CircleCheck className="text-primary mt-0.5 size-4 shrink-0" /> : <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />}
          <p className="flex-1">{notice}</p>
          <button
            type="button"
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => void navigate({ to: "/settings", search: {}, hash: "billing", replace: true })}
          >
            <X className="size-4" />
          </button>
        </div>
      )}
      {me.isPending ? (
        <Skeleton className="h-12" />
      ) : me.isError ? (
        <p className="text-destructive text-sm">Couldn't load your plan.</p>
      ) : (
        <>
          <PlanStatus user={me.data} />
          {me.data.plan !== "pro" && <PlanCard plan="monthly" price="$8" per="month" />}
        </>
      )}
    </Section>
  )
}

function PlanStatus({ user }: { user: User }) {
  const { label, detail } =
    user.plan === "pro"
      ? user.cancelAtPeriodEnd
        ? { label: "Cancelled", detail: user.planRenewsAt && `Access until ${formatDate(user.planRenewsAt)}. It won't renew.` }
        : { label: "Pro", detail: user.planRenewsAt && `Renews ${formatDate(user.planRenewsAt)}.` }
      : user.uatExpiresAt
        ? { label: "UAT access", detail: `UAT access through ${formatUatThroughDate(user.uatExpiresAt)}.` }
        : user.plan === "trialing"
          ? { label: "Trial", detail: user.trialEndsAt && `Trial ends ${formatDate(user.trialEndsAt)}.` }
          : { label: "Expired", detail: "Your nooks are safe. Subscribe to Nook and Ask." }

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-sm font-medium">
          Current plan <Badge variant={label === "Expired" ? "destructive" : "secondary"}>{label}</Badge>
        </p>
        {detail && <p className="text-muted-foreground text-xs">{detail}</p>}
      </div>
      {user.hasBillingAccount && <ManageButton />}
    </div>
  )
}

function ManageButton() {
  const portal = useOpenBillingPortal()

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={portal.isPending || portal.isSuccess}
      onClick={() =>
        portal.mutate(undefined, {
          onSuccess: (url) => window.location.assign(url),
          onError: () => toast.error("Couldn't open billing. Try again."),
        })
      }
    >
      Manage subscription
    </Button>
  )
}

/** The one boxed element on the page: it is the upgrade call to action. */
function PlanCard({ plan, price, per }: { plan: CheckoutPlan; price: string; per: string }) {
  const checkout = useStartCheckout(plan)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nook Pro</CardTitle>
        <CardDescription>
          <span className="text-foreground text-2xl font-semibold">{price}</span> / {per}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          className="w-full"
          disabled={checkout.isPending || checkout.isSuccess}
          onClick={() =>
            checkout.mutate(undefined, {
              onSuccess: (url) => window.location.assign(url),
              onError: () => toast.error("Couldn't start checkout. Try again."),
            })
          }
        >
          Upgrade
        </Button>
      </CardContent>
    </Card>
  )
}

function LinkedChannels() {
  const channels = useLinkedChannels()

  // Not on this deployment: no section at all, rather than a button that can only fail.
  if (channels.data?.available === false) return null

  return (
    <Section id="whatsapp" title="WhatsApp" description="Nook things by sending them to Nook on WhatsApp.">
      {channels.isPending ? (
        <Skeleton className="h-12" />
      ) : !channels.data ? (
        <p className="text-destructive text-sm">Couldn't load your linked channels.</p>
      ) : (
        <>
          {channels.data.items.length > 0 && (
            <ul className="divide-border divide-y">
              {channels.data.items.map((channel) => (
                <ChannelRow key={channel.id} channel={channel} />
              ))}
            </ul>
          )}
          <LinkWhatsapp count={channels.data.items.length} />
        </>
      )}
    </Section>
  )
}

function ChannelRow({ channel }: { channel: LinkedChannel }) {
  const unlink = useUnlinkChannel()

  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium">
          WhatsApp{channel.phoneHint && ` •••• ${channel.phoneHint}`}
        </p>
        <p className="text-muted-foreground text-xs">Linked {formatDate(channel.linkedAt)}</p>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" disabled={unlink.isPending}>
            Revoke
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription>
              Messages from this number stop reaching your Nook. Nooks from this number stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => unlink.mutate(channel.id, { onError: () => toast.error("Couldn't revoke. Try again.") })}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

function LinkWhatsapp({ count }: { count: number }) {
  const linkCode = useCreateLinkCode()
  const secondsLeft = useSecondsUntil(linkCode.data?.expiresAt)
  const first = count === 0

  // Poll while a live code is showing; a new row means the code was used.
  useLinkedChannels({ refetchInterval: linkCode.data && secondsLeft > 0 ? 3000 : false })
  const { reset } = linkCode
  const prevCount = useRef(count)
  useEffect(() => {
    if (count > prevCount.current) reset()
    prevCount.current = count
  }, [count, reset])

  if (linkCode.error instanceof NookApiError && linkCode.error.code === "whatsapp_unconfigured") {
    return <p className="text-muted-foreground text-sm">WhatsApp linking isn't available yet.</p>
  }

  if (!linkCode.data) {
    return (
      <div className="space-y-2">
        {linkCode.isError && <p className="text-destructive text-sm">{linkCode.error.message}</p>}
        <Button variant={first ? "default" : "outline"} onClick={() => linkCode.mutate()} disabled={linkCode.isPending}>
          {first ? "Link WhatsApp" : "Link another number"}
        </Button>
      </div>
    )
  }

  const expired = secondsLeft <= 0

  return (
    <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left">
      {/* Dark modules on a light ground in both themes: some scanners reject an inverted code. */}
      <div
        className={`bg-background text-foreground dark:bg-foreground dark:text-background rounded-md border p-3 ${expired ? "opacity-30" : ""}`}
      >
        <QRCodeSVG value={linkCode.data.url} size={160} bgColor="transparent" fgColor="currentColor" />
      </div>

      <div className="space-y-3">
        <p className="text-sm">
          Scan with your phone, or{" "}
          <a href={linkCode.data.url} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-4">
            open WhatsApp
          </a>{" "}
          and send the message it fills in.
        </p>
        <p className="text-muted-foreground font-mono text-sm">link {linkCode.data.code}</p>
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {expired
            ? "This code has expired."
            : `Expires in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`}
        </p>
        <Button variant="outline" size="sm" onClick={() => linkCode.mutate()} disabled={linkCode.isPending}>
          <RefreshCw /> New code
        </Button>
      </div>
    </div>
  )
}

function useSecondsUntil(iso: string | undefined) {
  const [now, setNow] = useState(Date.now)

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  return iso ? Math.max(0, Math.floor((Date.parse(iso) - now) / 1000)) : 0
}

/** MCP tokens (block 27): list, revoke, and a create flow that shows the connector link once. */
function McpTokens() {
  const tokens = useMcpTokens()

  return (
    <Section id="mcp" title="MCP" description="Let Claude Desktop, Cursor or another MCP client search and read your nooks.">
      {tokens.isPending ? (
        <Skeleton className="h-12" />
      ) : tokens.isError ? (
        <p className="text-destructive text-sm">Couldn't load your MCP tokens.</p>
      ) : (
        tokens.data.items.length > 0 && (
          <ul className="divide-border divide-y">
            {tokens.data.items.map((token) => (
              <McpTokenRow key={token.id} token={token} />
            ))}
          </ul>
        )
      )}
      <CreateMcpToken />
    </Section>
  )
}

function McpTokenRow({ token }: { token: McpToken }) {
  const revoke = useRevokeMcpToken()

  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{token.name}</p>
        <p className="text-muted-foreground text-xs">
          Created {formatDate(token.createdAt)} · Last used {token.lastUsedAt ? formatDate(token.lastUsedAt) : "Never"}
        </p>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" disabled={revoke.isPending}>
            Revoke
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {token.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Any client using this link loses access to your Nook immediately. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => revoke.mutate(token.id, { onError: () => toast.error("Couldn't revoke. Try again.") })}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

function CreateMcpToken() {
  const create = useCreateMcpToken()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [copied, setCopied] = useState(false)

  const onOpenChange = (next: boolean) => {
    // Closing mid-request would mint a token whose link is never shown.
    if (!next && create.isPending) return
    setOpen(next)
    // Dropping the result on close is the point: the link is never shown twice.
    if (!next) {
      create.reset()
      setName("")
      setCopied(false)
    }
  }

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      toast.error("Couldn't copy. Select the link and copy it.")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">Create token</Button>
      </DialogTrigger>

      <DialogContent>
        {create.data ? (
          <>
            <DialogHeader>
              <DialogTitle>Connect {create.data.name}</DialogTitle>
              <DialogDescription>Paste this link into your MCP client's connector settings.</DialogDescription>
            </DialogHeader>

            <div className="flex gap-2">
              <Input
                readOnly
                value={create.data.connectorUrl}
                aria-label="Connector link"
                className="font-mono text-xs"
                onFocus={(event) => event.currentTarget.select()}
              />
              <Button variant="outline" onClick={() => void copy(create.data.connectorUrl)}>
                {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <p role="alert" className="border-destructive/50 text-destructive rounded-md border p-3 text-sm">
              You won't see this link again. Copy it now. Anyone with it can read your nooks, so keep it private.
            </p>

            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              create.mutate(name.trim())
            }}
          >
            <DialogHeader>
              <DialogTitle>Create an MCP token</DialogTitle>
              <DialogDescription>Name it after the client you'll use it in.</DialogDescription>
            </DialogHeader>

            <Input
              autoFocus
              value={name}
              maxLength={60}
              placeholder="Claude Desktop"
              aria-label="Token name"
              onChange={(event) => setName(event.target.value)}
            />
            {create.isError && <p className="text-destructive text-sm">{create.error.message}</p>}

            <DialogFooter>
              <Button type="submit" disabled={!name.trim() || create.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** The export (block 33), on every plan. */
function ExportData({ onExport, pending, sent }: { onExport: () => void; pending: boolean; sent: boolean }) {
  return (
    <Section title="Your data" description="A copy of everything you've nooked.">
      {sent ? (
        <p role="status" className="text-sm">
          Your export is on its way. We'll email you a download link when it's ready, usually within a few minutes.
          You can close this page.
        </p>
      ) : (
        <Button variant="outline" onClick={onExport} disabled={pending}>
          <Download /> Export my data
        </Button>
      )}
      <p className="text-muted-foreground text-xs">
        A JSON file with the link and your note for every nook. The link lasts 7 days.
      </p>
    </Section>
  )
}

/**
 * Delete in one step, behind typing "delete" (block 34 override 1). The dialog
 * offers the export first, because block 33 exists so nobody destroys what they
 * could keep. Success goes to `/signin`, whose `account_deleted` path signs out
 * and says how to restore.
 */
function DeleteAccount({ onExport, exported }: { onExport: () => void; exported: boolean }) {
  const deleteAccount = useDeleteAccount()
  const navigate = useNavigate()
  const [typed, setTyped] = useState("")

  const onDelete = () =>
    deleteAccount.mutate(undefined, {
      onSuccess: () => void navigate({ href: "/signin?error_code=account_deleted" }),
      onError: (error) =>
        toast.error(
          error instanceof NookApiError && error.code === "rate_limited"
            ? "Too many attempts today. Try again tomorrow."
            : "Couldn't delete your account. Nothing was deleted. Try again.",
        ),
    })

  return (
    <Section danger title="Danger zone" description="Delete your account and everything in it.">
      <AlertDialog onOpenChange={() => setTyped("")}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" className="text-destructive hover:text-destructive">
            Delete my account
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Every nook, image, tag, Ask conversation, MCP link and linked WhatsApp number goes. Any subscription is
                  cancelled right away, with no refund.
                </p>
                <p>Everything is erased for good after 60 days. Until then, support can restore it.</p>
                <p>Want a copy first? Export your data before you delete.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="confirm-delete" className="text-sm">
              Type <span className="font-semibold">delete</span> to confirm.
            </label>
            <Input
              id="confirm-delete"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={deleteAccount.isPending}
            />
          </div>
          <AlertDialogFooter>
            <Button variant="outline" onClick={onExport} disabled={exported}>
              <Download /> {exported ? "Export requested" : "Export my data"}
            </Button>
            <AlertDialogCancel disabled={deleteAccount.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={typed.trim().toLowerCase() !== "delete" || deleteAccount.isPending}
            >
              {deleteAccount.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  )
}
